import React from 'react';
import { ArrowRight, Eye, Target, Heart, Users, BookOpen, GraduationCap, Flame, Settings, Fingerprint, Shield, Users2, Lightbulb } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSiteContent } from '../context/SiteContentContext';
import LanguageAwareVideo from '../components/common/LanguageAwareVideo';
import { buildSubtitleTracks } from '../utils/subtitles';

const valueIconMap = {
  passion: Flame,
  flexibility: Settings,
  individuality: Fingerprint,
  integrity: Shield,
  teamwork: Users2,
  creativity: Lightbulb
};

const impactIcons = [Users, BookOpen, GraduationCap];

const About = () => {
  const { siteContent } = useSiteContent();
  const aboutPage = siteContent?.aboutPage || {};
  const values = aboutPage.values || [];
  const impactItems = aboutPage.impactItems || [];

  return (
    <div data-testid="about-page" className="bg-white">
      {/* Header */}
      <section className="bg-slate-50 py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center"
          >
            <span className="text-xs font-medium text-slate-400 uppercase tracking-[0.2em] mb-5 block">
              {aboutPage.headerLabel || 'About Us'}
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              {aboutPage.headerTitle || 'Who We Are'}
            </h1>
            <p className="text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto">
              {aboutPage.headerSubtitle || 'English Passion was created with a simple belief: every student deserves a teacher who understands their needs.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Eye className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
                {aboutPage.visionTitle || 'Our Vision'}
              </h2>
            </div>
            <p className="text-lg text-slate-600 leading-[1.8]">
              {aboutPage.visionText || 'English Passion aims to be a premier center for high-quality English education.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="border-t border-slate-100"></div>
      </div>

      {/* Mission Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
                {aboutPage.missionTitle || 'Our Mission'}
              </h2>
            </div>
            <p className="text-lg text-slate-600 leading-[1.8]">
              {aboutPage.missionText || 'Our mission is to deliver personalized, high-quality English instruction.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-14 md:mb-16"
          >
            <div className="w-12 h-12 bg-slate-200/60 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Heart className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
              {aboutPage.valuesTitle || 'Our Core Values'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {values.map((value, index) => {
              const Icon = valueIconMap[value.key] || Heart;
              return (
                <motion.div
                  key={value.key || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.5, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-white rounded-xl p-6 border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-4.5 h-4.5 text-slate-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 mb-2 tracking-tight">
                    {value.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-14"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
              {aboutPage.impactTitle || 'Our Impact'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {impactItems.map((item, index) => {
              const Icon = impactIcons[index % 3];
              const itemKey = item.title?.en || item.title || `impact-${index}`;
              return (
                <motion.div
                  key={itemKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.5, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center"
                >
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-slate-600" strokeWidth={1.5} />
                  </div>
                  <p className="font-semibold text-slate-900 text-lg mb-1">{item.title}</p>
                  <p className="text-sm text-slate-500">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Scholars Video Section */}
      {siteContent?.scholarsVideo?.visible !== false && (
        <section className="py-20 md:py-28 bg-slate-50">
          <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Heart className="w-6 h-6 text-rose-600" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                {siteContent?.scholarsVideo?.sectionTitle || 'Changing Lives Through Education'}
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                {siteContent?.scholarsVideo?.sectionSubtitle || 'Meet the four scholars whose dreams we helped make possible — from their first English lessons to university graduation'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/10 bg-slate-900"
            >
              <LanguageAwareVideo
                videoUrl={siteContent?.scholarsVideo?.videoUrl || "/videos/scholars.mp4"}
                subtitles={buildSubtitleTracks(siteContent?.scholarsVideo?.subtitles)}
                testId="scholars-video"
                containerClassName="bg-slate-900"
              />
            </motion.div>
          </div>
        </section>
      )}

      {/* Story Section */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="order-2 lg:order-1"
            >
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-8 tracking-tight">
                {aboutPage.storyTitle || 'Our Story'}
              </h2>
              <div className="space-y-5">
                <p className="text-slate-600 leading-[1.8]">
                  {aboutPage.storyText1 || 'English Passion began with a simple belief: every student deserves a teacher who understands their needs.'}
                </p>
                <p className="text-slate-600 leading-[1.8]">
                  {aboutPage.storyText2 || 'What started as one-on-one tutoring has grown into a community of dedicated educators and enthusiastic learners.'}
                </p>
                <p className="text-slate-600 leading-[1.8]">
                  {aboutPage.storyText3 || 'Today, we continue to grow while staying true to our founding principles.'}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="order-1 lg:order-2"
            >
              <div className="rounded-2xl overflow-hidden">
                <img
                  src={siteContent?.images?.storyImage || 'https://images.unsplash.com/photo-1631947430066-48c30d57b943?q=80&w=2000&auto=format&fit=crop'}
                  alt="Our Story"
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-slate-900">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight">
              {aboutPage.ctaTitle || 'Ready to Start Your Journey?'}
            </h2>
            <p className="text-base text-slate-400 mb-10 max-w-xl mx-auto leading-relaxed">
              {aboutPage.ctaSubtitle || 'Join hundreds of students who have improved their English with our patient, expert guidance.'}
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="inline-flex items-center justify-center bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-3.5 text-sm font-medium transition-all"
            >
              {aboutPage.ctaButton || 'Book a Free Trial Lesson'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
